import { useState, useEffect } from "react";
import {
  Search,
  ChevronDown,
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  Copy,
  Check
} from "lucide-react";
import type { Finding } from "./types";

interface VulnerabilitiesTabProps {
  findings: Finding[];
  initialSeverity?: string;
}

export default function VulnerabilitiesTab({
  findings,
  initialSeverity = "ALL",
}: VulnerabilitiesTabProps) {
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState(initialSeverity);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Keep filter in sync if user clicks from overview tab
  useEffect(() => {
    setSeverityFilter(initialSeverity);
    setExpandedIndex(null);
  }, [initialSeverity]);

  if (!findings || findings.length === 0) {
    return (
      <div className="py-12 border border-dashed border-gray-300 dark:border-gray-800 rounded-xl text-center text-gray-500 dark:text-gray-400 italic">
        No vulnerability findings discovered.
      </div>
    );
  }

  // Helper styles for severity
  const getSeverityStyles = (severity: string) => {
    switch (severity.toUpperCase()) {
      case "CRITICAL":
        return {
          badge: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-400 border",
          border: "border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-500/25 transition-all duration-200",
          cardBg: "bg-white dark:bg-gradient-to-r dark:from-red-500/[0.03] dark:via-red-500/[0.005] dark:to-transparent",
        };
      case "HIGH":
        return {
          badge: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-400 border",
          border: "border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-500/25 transition-all duration-200",
          cardBg: "bg-white dark:bg-gradient-to-r dark:from-orange-500/[0.03] dark:via-orange-500/[0.005] dark:to-transparent",
        };
      case "MEDIUM":
        return {
          badge: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-450 border",
          border: "border border-gray-200 dark:border-gray-700 hover:border-yellow-300 dark:hover:border-yellow-500/25 transition-all duration-200",
          cardBg: "bg-white dark:bg-gradient-to-r dark:from-yellow-500/[0.03] dark:via-yellow-500/[0.005] dark:to-transparent",
        };
      case "LOW":
        return {
          badge: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-400 border",
          border: "border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500/25 transition-all duration-200",
          cardBg: "bg-white dark:bg-gradient-to-r dark:from-blue-500/[0.03] dark:via-blue-500/[0.005] dark:to-transparent",
        };
      default:
        return {
          badge: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-500/10 dark:border-gray-500/30 dark:text-gray-200 border",
          border: "border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200",
          cardBg: "bg-white dark:bg-gradient-to-r dark:from-gray-500/[0.03] dark:via-gray-500/[0.005] dark:to-transparent",
        };
    }
  };

  // Filtering
  const filteredFindings = findings.filter((f) => {
    const matchesSearch =
      f.finding.toLowerCase().includes(search.toLowerCase()) ||
      f.risk_description.toLowerCase().includes(search.toLowerCase()) ||
      f.module.toLowerCase().includes(search.toLowerCase());
    const matchesSeverity = severityFilter === "ALL" || f.severity.toUpperCase() === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Filters Toolbar */}
      <div className="flex flex-col lg:flex-row items-center gap-3 bg-gray-50/70 dark:bg-gray-800/40 p-3 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
          <input
            type="text"
            placeholder="Search vulnerabilities, risk summaries, modules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg pl-9 pr-4 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
          />
        </div>

        <div className="flex items-center gap-2 w-full lg:w-auto overflow-x-auto py-1">
          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest whitespace-nowrap">
            Severity:
          </span>
          <div className="flex gap-1.5">
            {["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"].map((sev) => (
              <button
                key={sev}
                onClick={() => {
                  setSeverityFilter(sev);
                  setExpandedIndex(null);
                }}
                className={`px-2.5 py-1 text-[9px] font-bold uppercase rounded-lg transition-colors whitespace-nowrap cursor-pointer ${severityFilter === sev
                  ? "bg-orange-600 text-white shadow shadow-orange-500/20"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-transparent"
                  }`}
              >
                {sev}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Accordion Findings list */}
      {filteredFindings.length > 0 ? (
        <div className="space-y-3">
          {filteredFindings.map((finding, idx) => {
            const isExpanded = expandedIndex === idx;
            const styles = getSeverityStyles(finding.severity);
            return (
              <div
                key={idx}
                className={`transition-all duration-200 overflow-hidden shadow-sm rounded-xl ${styles.cardBg} ${styles.border} ${isExpanded ? "border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30" : ""
                  }`}
              >
                {/* Accordion Header */}
                <div
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                  className="flex items-center justify-between p-4 cursor-pointer select-none"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0 pr-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase font-mono tracking-wider shrink-0 ${styles.badge}`}>
                      {finding.severity}
                    </span>
                    <span className="font-bold text-xs text-gray-900 dark:text-gray-200 truncate">
                      {finding.finding}
                    </span>
                    {finding.port && finding.port !== "N/A" && (
                      <span className="text-[9px] font-mono text-cyan-700 dark:text-cyan-400 font-bold shrink-0 border border-cyan-200 dark:border-cyan-500/15 px-1.5 py-0.2 rounded bg-cyan-50 dark:bg-cyan-500/5">
                        {finding.port}
                      </span>
                    )}
                    <span className="text-[9px] font-mono text-gray-600 dark:text-gray-300 font-bold uppercase shrink-0 border border-gray-200 dark:border-gray-800 px-1.5 py-0.2 rounded bg-gray-100 dark:bg-gray-900/40">
                      {finding.module.replace(/_/g, " ")}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                </div>

                {/* Expanded Content Panel */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-800 px-5 py-5 space-y-4 bg-gray-50/50 dark:bg-gray-900/25">

                    {/* Grid Details Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                      {/* Left Block: Description and Recommendations */}
                      <div className="md:col-span-2 space-y-4">
                        <div>
                          <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                            <HelpCircle size={11} /> Threat / Vulnerability Description
                          </h4>
                          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed font-semibold">
                            {finding.risk_description || "No threat description available."}
                          </p>
                        </div>

                        {finding.recommendation && (
                          <div className="bg-orange-50 dark:bg-orange-500/[0.02] border border-orange-200 dark:border-orange-500/10 rounded-xl p-3.5 space-y-1.5">
                            <h4 className="text-[10px] font-bold text-orange-700 dark:text-orange-400 uppercase tracking-widest flex items-center gap-1.5 leading-none">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Remediation Recommendation
                            </h4>
                            <p className="text-xs text-gray-700 dark:text-gray-250 leading-relaxed font-medium">
                              {finding.recommendation}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Right Block: Meta details & References */}
                      <div className="space-y-4 md:border-l md:border-gray-200 dark:md:border-gray-800 md:pl-5">
                        {/* Target Context */}
                        {finding.url && (
                          <div>
                            <h4 className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">
                              Impacted Target
                            </h4>
                            <a
                              href={finding.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1 font-mono break-all font-semibold"
                            >
                              {finding.url}
                              <ExternalLink size={10} className="shrink-0" />
                            </a>
                          </div>
                        )}

                        {/* Classifications */}
                        {finding.classification && Object.keys(finding.classification).length > 0 && (
                          <div>
                            <h4 className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">
                              Security Standards Mapping
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {finding.classification.cwe && (
                                <span className="text-[9px] px-2 py-0.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 font-mono font-bold rounded-lg" title="Common Weakness Enumeration">
                                  {finding.classification.cwe}
                                </span>
                              )}
                              {finding.classification.owasp_2021 && (
                                <span className="text-[9px] px-2 py-0.5 bg-orange-50 dark:bg-gray-800 border border-orange-200 dark:border-gray-800 text-orange-700 dark:text-orange-350 font-mono font-bold rounded-lg" title="OWASP Top 10 Reference">
                                  {finding.classification.owasp_2021}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* References */}
                        {finding.references && finding.references.length > 0 && (
                          <div>
                            <h4 className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">
                              References & Manuals
                            </h4>
                            <div className="space-y-1.5">
                              {finding.references.map((ref, rIdx) => (
                                <a
                                  key={rIdx}
                                  href={ref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[10px] text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1.5 max-w-full truncate font-semibold"
                                >
                                  <ExternalLink className="w-3 h-3 shrink-0 text-orange-500/70" />
                                  <span className="truncate">{ref}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom Console Evidence Block */}
                    {finding.evidence && (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-1">
                            Scan Evidence Proof
                          </h4>
                          <button
                            onClick={() => handleCopy(
                              typeof finding.evidence === "string" ? finding.evidence : JSON.stringify(finding.evidence, null, 2),
                              idx
                            )}
                            className="flex items-center gap-1 text-[9px] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-bold transition-colors bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-800 px-2 py-1 rounded-md cursor-pointer"
                          >
                            {copiedIndex === idx ? (
                              <>
                                <Check size={11} className="text-emerald-500 dark:text-emerald-400" /> Copied!
                              </>
                            ) : (
                              <>
                                <Copy size={11} /> Copy Evidence
                              </>
                            )}
                          </button>
                        </div>
                        <div className="relative border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-inner">
                          <div className="absolute top-0 left-0 right-0 h-7 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500/30 inline-block"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/30 inline-block"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-green-500/30 inline-block"></span>
                            <span className="text-[9px] font-mono font-bold text-gray-600 dark:text-gray-200 uppercase ml-2 select-none tracking-wider">proof_console.log</span>
                          </div>
                          <pre className="p-4 pt-10 bg-gray-50 dark:bg-gray-800 text-[10px] font-mono text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap max-h-56 overflow-y-auto leading-relaxed custom-scrollbar">
                            {typeof finding.evidence === "string"
                              ? finding.evidence
                              : JSON.stringify(finding.evidence, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-12 border border-dashed border-gray-300 dark:border-gray-800 rounded-xl text-center text-gray-500 dark:text-gray-400 italic">
          No vulnerabilities match the selected severity and filters.
        </div>
      )}
    </div>
  );
}
